import { useEffect, useState } from "react";
import { ArrowLeft, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { adminFetch } from "./adminApi";

interface PaymentFailure {
  eventId: number;
  bookingId: number;
  bookingCode?: string;
  senderName?: string;
  senderPhone?: string;
  amount: number;
  status: string;
  reason?: string;
  createdAt: string;
}

export default function AdminPayments({ onBack }: { onBack: () => void }) {
  const [failures, setFailures] = useState<PaymentFailure[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`/api/admin/payments/failures`);
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      setFailures(payload.failures || []);
    } catch (e) {
      toast.error("Failed to load payment failures.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const handleRefund = async (bookingId: number) => {
    const amountStr = window.prompt("Amount to refund/credit (₹):");
    if (!amountStr || !(Number(amountStr) > 0)) return;
    const type = window.prompt("Type: 'refund' or 'credit'?", "refund");
    if (type !== "refund" && type !== "credit") { toast.error("Type must be 'refund' or 'credit'."); return; }
    const reason = window.prompt("Reason (required):");
    if (!reason) return;
    try {
      const res = await adminFetch(`/api/admin/payments/${bookingId}/refund`, {
        method: "POST",
        body: JSON.stringify({ amount: Number(amountStr), type, reason }),
      });
      const payload = await res.json();
      if (!res.ok || !payload?.success) throw new Error(payload?.message);
      toast.success(`${type === "refund" ? "Refund" : "Credit"} issued — rider's wallet updated.`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to issue refund/credit.");
    }
  };

  const handleManualRefund = async () => {
    const bookingId = window.prompt("Booking ID:");
    if (!bookingId) return;
    await handleRefund(Number(bookingId));
  };

  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg bg-[#0c1428] hover:bg-[#0f1e3d] text-[#a0aec0]"><ArrowLeft size={16} /></button>
          <h1 className="text-white text-lg sm:text-xl font-semibold">Payments</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleManualRefund} className="px-3 py-2 bg-[#00c9a7]/10 hover:bg-[#00c9a7]/20 border border-[#00c9a7]/30 rounded-lg text-[#00c9a7] text-sm">Issue refund / credit</button>
          <button onClick={() => void load()} className="p-2 bg-[#0c1428] hover:bg-[#0f1e3d] border border-white/[0.07] rounded-lg text-[#a0aec0]"><RefreshCw size={16} /></button>
        </div>
      </div>

      <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl px-4 py-3 text-xs mb-5">
        <AlertTriangle size={14} className="shrink-0 mt-0.5" />
        No payment gateway is wired to AfdaniTransport bookings yet — this list only shows manually-recorded events. Refunds/credits here add directly to the rider's Afdani wallet.
      </div>

      {loading ? (
        <div className="text-center text-[#6b7fa3] text-sm py-14">Loading…</div>
      ) : (
        <div className="bg-[#0c1428] border border-white/[0.07] rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#6b7fa3] border-b border-white/[0.06]">
                <th className="px-4 py-3">Booking</th>
                <th className="px-4 py-3">Sender</th>
                <th className="px-4 py-3">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {failures.map((f) => (
                <tr key={f.eventId} className="border-b border-white/[0.04] text-[#e2e8f0]">
                  <td className="px-4 py-3 font-mono text-xs">{f.bookingCode || f.bookingId}</td>
                  <td className="px-4 py-3">{f.senderName} <span className="text-[#6b7fa3]">{f.senderPhone}</span></td>
                  <td className="px-4 py-3">₹{f.amount}</td>
                  <td className="px-4 py-3">{f.status}</td>
                  <td className="px-4 py-3 text-xs text-[#a0aec0]">{f.reason}</td>
                  <td className="px-4 py-3 text-xs text-[#6b7fa3]">{new Date(f.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleRefund(f.bookingId)} className="px-3 py-1.5 bg-[#0a1020] hover:bg-[#0f1e3d] border border-white/[0.08] rounded-lg text-[#a0aec0] text-xs">Refund/credit</button>
                  </td>
                </tr>
              ))}
              {failures.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6b7fa3]">No payment failures recorded.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
